// CvEntityCard — what a canvas item renders when its key is an entity ref
// (`ref:<type>:<id>`) instead of a project page / component key.
//
// It sits exactly where `CvFrame` sits for a page tile: as the ONE child of
// CanvasItem, filling the item box. CanvasItem still owns the geometry, the
// pointer gestures (select / drag / marquee), the context menu and the
// selection chrome — this component only paints, so an entity node behaves
// like every other node on the board for free.
//
// ── WHY IT DOES NOT DECLARE ITS OWN `state` ───────────────────────────────
// It shares CanvasItem's ITEM state — the per-tile slice CvItems seeds through
// `childrenAs: 'state'` (`{ key, pageKey, type, _cvType, … }`) — and writes the
// resolved entity back onto it under `__entity` / `__entityLoading` /
// `__entityFailed`.
//
// This is not a style choice; the first cut declared `state: { entity, … }` and
// the card NEVER RENDERED. A child that declares its own `state` gets a NEW
// store, so `s._cvType` inside this component's `if:` read `undefined`, the
// gate evaluated false, and the element was never created — silently, with the
// item box, the hidden iframe and zero console output (measured live
// 2026-09-07: `CvEntityCard.__ref.__if === false` on a tile whose item state
// carried `_cvType: 'entity'`).
//
// Sharing the item state also gets the isolation the separate slice was for:
// the item state is PER TILE, not the root store, so a resolve repaints one
// card. The shared `canvas.entities` cache is written quietly alongside it
// (`preventUpdate`) because that is what the sidebar, Expand and the AI
// attachment read synchronously.
//
// The `__`-prefixed names mark fields the CARD owns on a state slice the
// SURFACE builds: CvItems' children factory returns fresh objects on every
// re-map, so a remount drops them — which is correct, because `onRender`
// re-resolves (from cache, synchronously) on the new element.
//
// ── POINTER RULES ─────────────────────────────────────────────────────────
// Everything except the two action buttons is inert (`pointerEvents: 'none'`
// on the text column) so a drag started anywhere on the card still reaches
// CanvasItem's `onPointerDown`. The buttons re-enable events for themselves
// and stop propagation on `pointerdown`, otherwise pressing Open would begin a
// drag and the click would never land.

// Every prop reads the SAME item state, so the two reads are folded into one
// helper each rather than repeated per prop.
const _entity = (s) => (s && s.__entity) || null
const _key = (s) => (s && s.key) || ''

export const CvEntityCard = {
  tag: 'div',
  position: 'absolute',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  flow: 'y',
  gap: 'Y',
  padding: 'Z A',
  overflow: 'hidden',
  theme: 'document',
  boxSizing: 'border-box',

  // Resolve on mount, mirror onto the item state. Guarded against the
  // double-init every canvas tile hits (Rule 15) — onRender can fire again
  // when the viewport windowing remounts a tile.
  //
  // Seeds SYNCHRONOUSLY from the shared cache first: a tile that scrolls back
  // into view, or a node placed by Expand, is already resolved, and waiting a
  // microtask to paint a value we already hold would flash 'Loading' for one
  // frame on every re-entry.
  onRender: (el, s) => {
    if (el.__cvEntityLoaded) return
    el.__cvEntityLoaded = true
    const key = _key(s)
    if (!key) return
    const cached = el.call('getCanvasEntity', key)
    if (cached && cached.data) {
      s.update({ __entity: cached.data, __entityLoading: false, __entityFailed: !!cached.error })
      return
    }
    s.update({ __entityLoading: true })
    Promise.resolve(el.call('resolveCanvasEntity', key))
      .then((entry) => {
        if (!el.node) return
        s.update({
          __entity: (entry && entry.data) || null,
          __entityLoading: false,
          __entityFailed: !!(entry && entry.error)
        })
      })
      .catch(() => {
        if (!el.node) return
        s.update({ __entityLoading: false, __entityFailed: true })
      })
  },

  Head: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    pointerEvents: 'none',
    // Kind badge. The icon comes from the ref TYPE; the label is the row's own
    // `kind` field (a data value — a code identifier when the row carries
    // none), so it is deliberately not routed through polyglot.
    KindBadge: {
      extends: 'Badge',
      theme: 'transparent',
      flow: 'x',
      gap: 'X',
      align: 'center left',
      fontSize: 'Y',
      padding: 'X Y',
      Icon: {
        boxSize: 'Z1 Z1',
        color: 'caption',
        name: (el, s) => el.call('entityRefIcon', _key(s))
      },
      Label: {
        tag: 'span',
        color: 'caption',
        whiteSpace: 'nowrap',
        text: (el, s) => {
          const e = _entity(s)
          return (e && e.kind) || ''
        }
      }
    },
    // "Sample data" marker — the truth boundary made visible. Present only
    // when the row itself carries `data.sample`.
    SampleTag: {
      extends: 'Badge',
      theme: 'warning',
      fontSize: 'Y',
      padding: 'X Y',
      text: '{{ canvas.entity.sample | polyglot }}',
      hide: (el, s) => {
        const e = _entity(s)
        return !(e && e.sample)
      }
    }
  },

  Title: {
    tag: 'span',
    fontSize: 'Z1',
    fontWeight: '700',
    color: 'title',
    lineHeight: '1.3',
    pointerEvents: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      const e = _entity(s)
      if (e && e.title) return e.title
      return s.__entityLoading
        ? el.call('polyglot', 'common.state.loading')
        : el.call('polyglot', 'canvas.entity.untitled')
    }
  },

  // Up to three lines of summary.
  Summary: {
    tag: 'p',
    margin: '0',
    flex: '1',
    minHeight: '0',
    fontSize: 'Y',
    color: 'subtitle',
    lineHeight: '1.45',
    overflow: 'hidden',
    pointerEvents: 'none',
    // `display` is a registered flat CSS prop and must NOT be smuggled through
    // `style:` (FA108). Only the two unregistered `-webkit-` keys need the raw
    // escape hatch — they are the only way to clamp to a LINE COUNT (a
    // max-height in em drifts with the resolved font size).
    display: '-webkit-box',
    style: { WebkitLineClamp: '3', WebkitBoxOrient: 'vertical' },
    text: (el, s) => {
      const e = _entity(s)
      if (e && e.summary) return e.summary
      return s.__entityFailed ? el.call('polyglot', 'canvas.entity.loadFailed') : ''
    }
  },

  // status · effectiveDate · collection — the separator is punctuation, not
  // copy, and the three values are record fields.
  Meta: {
    tag: 'span',
    fontSize: 'Y',
    color: 'caption',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
    text: (el, s) => {
      const e = _entity(s)
      if (!e) return ''
      return [e.status, e.effectiveDate, e.collection].filter(Boolean).join(' · ')
    }
  },

  Actions: {
    flow: 'x',
    gap: 'Y',
    align: 'center left',
    childExtends: 'CvEntityCardAction',
    OpenBtn: {
      Icon: { name: 'arrowUpRight' },
      title: (el) => el.call('polyglot', 'common.action.open'),
      aria: { label: (el) => el.call('polyglot', 'common.action.open') },
      // No route → the control is visibly inert rather than silently doing
      // nothing when pressed (a record whose module stamped no href, or a
      // canvas ref this package cannot resolve to an /<owner>/<key> route).
      // `null`, never `false`: an attribute prop that returns false still
      // RENDERS the attribute (SYNTAX — only null/undefined remove it).
      disabled: (el, s) => (el.call('entityRefHref', _key(s)) ? null : true),
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('openCanvasEntity', _key(s))
      }
    },
    ExpandBtn: {
      Icon: { name: 'expand' },
      title: (el) => el.call('polyglot', 'canvas.entity.expand'),
      aria: { label: (el) => el.call('polyglot', 'canvas.entity.expand') },
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        el.call('expandCanvasEntity', _key(s))
      }
    }
  }
}

// The card's action control. Extracted so hover / focus / disabled are DECLARED
// ONCE on the primitive (Rule 65) instead of on each of the two call sites, and
// so the pointer-gesture guard below cannot drift between them.
export const CvEntityCardAction = {
  tag: 'button',
  type: 'button',
  pointerEvents: 'auto',
  flow: 'x',
  align: 'center center',
  gap: 'X',
  width: 'B1',
  height: 'B1',
  round: 'radiusControl',
  borderWidth: '0',
  background: 'transparent',
  color: 'caption',
  cursor: 'pointer',
  lineHeight: '1',
  ':hover': { background: 'hover', color: 'title' },
  ':active': { background: 'selected' },
  ':focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },
  ':disabled': { opacity: '0.35', pointerEvents: 'none' },
  // CanvasItem starts a drag from `onPointerDown`; without this the press that
  // begins a click on this button also begins a drag of the whole node, and the
  // click never arrives.
  onPointerdown: (e) => e.stopPropagation(),
  // The glyph itself is set per call site (`Icon: { name: 'expand' }`); this
  // pins the SIZE once so the two actions can never drift apart.
  Icon: { boxSize: 'Z2 Z2' }
}
